import React from 'react';
import Image from 'next/image';

import { benefitsByState } from 'lib/data/benefits-by-state';
import { stateCookiesSlug } from 'lib/data/state-cookies-slug';

type BenefitKey =
  | 'accessToDispensaries'
  | 'taxSavings'
  | 'legalProtection'
  | 'accessToProducts'
  | 'lowerAgeRestriction'
  | 'cultivation'
  | 'higherPossession'
  | 'leafwellMoneyBack'
  | 'leafwellOnline'
  | 'leafwellDoctors';

type Benefit = { key: BenefitKey; title: string };
type StateBenefits = { name: string; benefits: Benefit[] };
type BenefitsData = {
  default: { name: string | null; benefits: Benefit[] };
  states: Record<string, StateBenefits>;
};

const data = benefitsByState as BenefitsData;

// When a state resolves but has no entry in the JSON (waitlist states such as
// Alabama, Idaho, Kansas, Puerto Rico), fall back to the generic Leafwell
// benefits. Set to false to render nothing instead.
const SHOW_DEFAULT_FOR_UNKNOWN_STATE = true;

const ICONS: Record<BenefitKey, string> = {
  accessToDispensaries: '/access-dispensaries-orange.svg',
  taxSavings: '/tax-savings-yellow.svg',
  legalProtection: '/legal-protection-purple.svg',
  accessToProducts: '/quality-products-green.svg',
  lowerAgeRestriction: '/age-possession-red.svg',
  cultivation: '/cultivation-blue.svg',
  higherPossession: '/higher-possession-pink.svg',
  leafwellMoneyBack: '/approved-money-back-green.svg',
  leafwellOnline: '/approved-money-back-green.svg',
  leafwellDoctors: '/approved-money-back-green.svg',
};

const slugByCode: Record<string, string> = stateCookiesSlug.reduce(
  (acc, { cookie, slug }) => ({ ...acc, [cookie]: slug }),
  {},
);

// "an Ohio" / "an Arkansas" / "an Illinois" — but "a Utah" (yoo-tah).
const label = (slug: string, name: string): string => {
  if (slug === 'washington-d-c')
    return 'With a medical card in Washington, D.C.';
  const article = /^[AEIOU]/.test(name) && slug !== 'utah' ? 'an' : 'a';
  return `With ${article} ${name} medical card`;
};

export const useHeroBenefits = (
  stateCode?: string | null,
): { label: string; benefits: Benefit[] } | null => {
  const slug = stateCode ? slugByCode[stateCode] : undefined;
  const state = slug ? data.states[slug] : undefined;

  if (state) {
    return {
      label: label(slug as string, state.name),
      benefits: state.benefits,
    };
  }

  // No state resolved yet, or a state with no benefits block of its own.
  if (!stateCode || SHOW_DEFAULT_FOR_UNKNOWN_STATE) {
    return {
      label: 'With a Leafwell medical card',
      benefits: data.default.benefits,
    };
  }

  return null;
};

type Props = {
  stateCode?: string | null;
  placement: 'desktop' | 'mobile';
  className?: string;
};

const HeroBenefits: React.FC<Props> = ({ stateCode, placement, className }) => {
  const content = useHeroBenefits(stateCode);

  if (!content) return null;

  const isDesktop = placement === 'desktop';
  const iconSize = isDesktop ? 48 : 40;

  return (
    <aside
      aria-label="Medical card benefits"
      className={className}
      style={{
        boxSizing: 'border-box',
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(255,255,255,0.9)',
        color: '#000F00',
        ...(isDesktop
          ? {
              width: 400,
              borderRadius: 16,
              padding: '22px 26px',
              WebkitBackdropFilter: 'blur(12px)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 10px 36px rgba(0,15,0,0.10)',
            }
          : {
              width: '100%',
              maxWidth: '28rem',
              borderRadius: 12,
              padding: '16px 18px',
            }),
      }}
    >
      <p
        style={{
          margin: isDesktop ? '0 0 14px' : '0 0 12px',
          fontSize: isDesktop ? 18 : 17,
          lineHeight: 1.2,
          fontWeight: 700,
          color: '#000F00',
        }}
      >
        {content.label}
      </p>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'grid',
          gap: isDesktop ? 14 : 12,
        }}
      >
        {content.benefits.map(({ key, title }) => (
          <li
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              margin: 0,
              padding: 0,
            }}
          >
            <Image
              src={ICONS[key]}
              alt=""
              width={iconSize}
              height={iconSize}
              style={{
                flex: `0 0 ${iconSize}px`,
                width: iconSize,
                height: iconSize,
                display: 'block',
              }}
            />
            <span
              style={{
                fontSize: isDesktop ? 19 : 17,
                lineHeight: 1.25,
                fontWeight: 600,
                color: '#000F00',
              }}
            >
              {title}
            </span>
          </li>
        ))}
      </ul>
    </aside>
  );
};

export default HeroBenefits;
